import { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { formatBoolean, formatCurrency } from 'helpers';
import moment from 'moment';
import {
  EstateBalanceSheetAsset,
  EstateBalanceSheetAssetColumns,
  EstateBalanceSheetLiability,
  EstateBalanceSheetLiabilityColumns,
  PortalPermissions,
  useEstate_GetEstateByIdQuery,
  useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery,
  useEstateShareMembership_GetEstateShareMembershipQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';
import * as XLSX from 'xlsx';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import EstateBalanceSheetDashboardHeader from 'pages/common/estate-balance-sheet/estate-balance-sheet-dashboard-header';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateBalanceSheetDashboardMenu from '../common/estate-balance-sheet/estate-balance-sheet-dashboard-menu';

import {
  allocateToOptions,
  assetTypeOptions,
  categoryOptions,
  distributionTypeOptions,
  lifetimeHoldingOptions,
  sourceOptions,
} from './components/estate-balance-sheet-assets';
import EstateBalanceSheetAssets from './components/estate-balance-sheet-assets';
import {
  allocateToOptions as liabilityAllocateToOptions,
  categoryOptions as liabilityCategoryOptions,
  sourceOptions as liabilitySourceOptions,
  statusOptions as liabilityStatusOptions,
} from './components/estate-balance-sheet-liabilities';
import EstateBalanceSheetLiabilities from './components/estate-balance-sheet-liabilities';

const useStyles = makeStyles(() => ({
  container: {
    color: '#1C2541',
  },
  btnWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
  },
  pill: {
    display: 'inline-block',
    background: '#F9F9F9',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
  },
  suggestionsLink: {
    fontSize: '12px',
    marginTop: '4px',
    color: '#1C2541',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '48px',
  },
  subTitle: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '24px',
  },
  actionButton: {
    background: '#F5F5F5',
    borderRadius: '8px',
    fontSize: '14px',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '16px',
    marginBottom: '16px',
  },
  actionButtonContainer: {
    marginBottom: '40px',
  },
  badges: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
}));

interface Props {
  hideNav?: boolean;
}

const EstateBalanceSheetPage = ({ hideNav = false }: Props) => {
  const history = useHistory();
  const classes = useStyles();
  const { estateId, portalId } = useParams<Record<string, string>>();

  const [assetsData, setAssetsData] = useState<EstateBalanceSheetAsset[]>([]);
  const [liabilitiesData, setLiabilitiesData] = useState<EstateBalanceSheetLiability[]>([]);

  const handleAssetsDataLoaded = useCallback((data: EstateBalanceSheetAsset[]) => {
    setAssetsData((prevData) => {
      const newData = data.filter(
        (newItem) => !prevData.some((prevItem) => prevItem.id === newItem.id),
      );
      return [...prevData, ...newData];
    });
  }, []);

  const handleLiabilitiesDataLoaded = useCallback((data: EstateBalanceSheetLiability[]) => {
    setLiabilitiesData((prevData) => {
      const newData = data.filter(
        (newItem) => !prevData.some((prevItem) => prevItem.id === newItem.id),
      );
      return [...prevData, ...newData];
    });
  }, []);

  const hasPortalPermissionClient = useHasPortalPermission(PortalPermissions.Client);

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: { id: estateId },
    onError: (e) =>
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const { data: estateBalanceSheetData, loading: estateBalanceSheetLoading } =
    useEstateBalanceSheet_GetEstateBalanceSheetByEstateIdQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) =>
        showAlert({
          message: 'Error getting balance sheet by estate ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        }),
    });

  const { data: estateShareMembershipData, loading: estateShareMembershipLoading } =
    useEstateShareMembership_GetEstateShareMembershipQuery({
      variables: { input: { estateId, portalId } },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate share membership: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (
    featureSubscriptionTypeLoading ||
    !featureSubscriptionTypeData ||
    estateLoading ||
    !estateData ||
    estateBalanceSheetLoading ||
    !estateBalanceSheetData?.data ||
    estateShareMembershipLoading ||
    !estateShareMembershipData
  ) {
    return <Spinner />;
  }

  if (hasPortalPermissionClient && !estateShareMembershipData.data?.isActive) {
    // If shared client user, send to home page if they don't have access to this estate
    history.replace(`/home/${portalId}/home`);
    return <Spinner />;
  }

  const estate = estateData.data;
  const estateBalanceSheet = estateBalanceSheetData.data;

  const autoSizeColumnsForExcelExport = (
    ws: XLSX.WorkSheet,
    data: (string | null | undefined)[][],
  ): void => {
    const colWidths = data[0].map((_, colIndex) => ({
      wch: Math.max(
        ...data.map((row) => (row[colIndex] != null ? row[colIndex]!.toString().length : 10)), // Handle null/undefined
      ),
    }));

    ws['!cols'] = colWidths;
  };

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      if (assetsData) {
        const assetsDataMapped = assetsData.map((asset) => [
          assetTypeOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.AssetType],
          )?.label,
          asset[EstateBalanceSheetAssetColumns.AssetDescription],
          lifetimeHoldingOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.LifetimeHolding],
          )?.label,
          asset[EstateBalanceSheetAssetColumns.InitialValue]
            ? formatCurrency(`${asset[EstateBalanceSheetAssetColumns.InitialValue] * 100.0}`)
            : undefined,
          asset[EstateBalanceSheetAssetColumns.InitialValueDate]
            ? moment(asset[EstateBalanceSheetAssetColumns.InitialValueDate]).format('MM/DD/YYYY')
            : '',
          asset[EstateBalanceSheetAssetColumns.CurrentValue]
            ? formatCurrency(`${asset[EstateBalanceSheetAssetColumns.CurrentValue] * 100.0}`)
            : undefined,
          asset[EstateBalanceSheetAssetColumns.CurrentValueDate]
            ? moment(asset[EstateBalanceSheetAssetColumns.CurrentValueDate]).format('MM/DD/YYYY')
            : '',
          distributionTypeOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.DistributionType],
          )?.label,
          asset[EstateBalanceSheetAssetColumns.NonProbateBeneficiaryName],
          formatBoolean(asset[EstateBalanceSheetAssetColumns.CanIncludeInTaxableEstate]),
          categoryOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.Category],
          )?.label,
          allocateToOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.AllocateTo],
          )?.label,
          asset[EstateBalanceSheetAssetColumns.Notes],
          sourceOptions.find(
            (option) => option.value === asset[EstateBalanceSheetAssetColumns.Source],
          )?.label,
        ]);

        const assetsHeader = [
          'Asset Type',
          'Asset Description',
          'How was this held during lifetime?',
          'Value',
          'Initial Value Date/DOD',
          'Post Death Appreciation',
          'Current Value Date',
          'Distribution Type',
          'Name of Beneficiary(ies) if Non-Probate',
          'Include in taxable estate?',
          'Category',
          'Allocate To',
          'Comment / Notes',
          'Source',
        ];

        const wsAssets = XLSX.utils.aoa_to_sheet([assetsHeader, ...assetsDataMapped]);
        autoSizeColumnsForExcelExport(wsAssets, [assetsHeader, ...assetsDataMapped]);
        XLSX.utils.book_append_sheet(wb, wsAssets, 'Assets');
      }

      if (liabilitiesData) {
        const liabilitiesDataMapped = liabilitiesData.map((liability) => [
          liability[EstateBalanceSheetLiabilityColumns.LiabilityName],
          liability[EstateBalanceSheetLiabilityColumns.LiabilityDescription],
          liability[EstateBalanceSheetLiabilityColumns.AccountInvoiceNumber],
          liability[EstateBalanceSheetLiabilityColumns.Balance]
            ? formatCurrency(`${liability[EstateBalanceSheetLiabilityColumns.Balance] * 100.0}`)
            : undefined,
          liability[EstateBalanceSheetLiabilityColumns.DueDate]
            ? moment(liability[EstateBalanceSheetLiabilityColumns.DueDate]).format('MM/DD/YYYY')
            : '',
          liability[EstateBalanceSheetLiabilityColumns.InterestRate]
            ? `${liability[EstateBalanceSheetLiabilityColumns.InterestRate]}%`
            : undefined,
          liabilityStatusOptions.find(
            (option) => option.value === liability[EstateBalanceSheetLiabilityColumns.Status],
          )?.label,
          liability[EstateBalanceSheetLiabilityColumns.PaidBy],
          formatBoolean(liability[EstateBalanceSheetLiabilityColumns.IsDeductibleExpense]),
          liabilityCategoryOptions.find(
            (option) => option.value === liability[EstateBalanceSheetLiabilityColumns.Category],
          )?.label,
          liabilityAllocateToOptions.find(
            (option) => option.value === liability[EstateBalanceSheetLiabilityColumns.AllocateTo],
          )?.label,
          liability[EstateBalanceSheetLiabilityColumns.Notes],
          liabilitySourceOptions.find(
            (option) => option.value === liability[EstateBalanceSheetLiabilityColumns.Source],
          )?.label,
        ]);

        const liabilitiesHeader = [
          'Name',
          'Liability Description (Company)',
          'Account # / Invoice #',
          'Balance',
          'Due Date',
          'Interest Rate',
          'Status',
          'Paid By, if Paid',
          'Deductible expense?',
          'Category',
          'Allocate To',
          'Comment / Notes',
          'Source',
        ];

        const wsLiabilities = XLSX.utils.aoa_to_sheet([
          liabilitiesHeader,
          ...liabilitiesDataMapped,
        ]);
        const range = XLSX.utils.decode_range(wsLiabilities['!ref']!);
        for (let col = range.s.c; col <= range.e.c; col++) {
          const headerCell = wsLiabilities[XLSX.utils.encode_cell({ r: 0, c: col })];
          if (headerCell) {
            headerCell.s = { font: { bold: true } }; // Apply bold font to the header cell
          }
        }
        autoSizeColumnsForExcelExport(wsLiabilities, [liabilitiesHeader, ...liabilitiesDataMapped]);
        XLSX.utils.book_append_sheet(wb, wsLiabilities, 'Liabilities');
      }

      XLSX.writeFile(wb, 'estate_balance_sheet.xlsx');
    } catch (error) {
      showAlert({
        message: 'Error downloading Excel file ',
        severity: NotificationSeverity.ERROR,
      });
    }
  };

  const content = (
    <Fade in={true}>
      <div className={classes.container}>
        <EstateBalanceSheetDashboardHeader estate={estate} pageTitle="Balance Sheet" />
        <EstateBalanceSheetDashboardMenu
          estate={estate}
          estateBalanceSheet={estateBalanceSheet}
          page="estate-balance-sheet"
          handleExportExcel={handleExportExcel}
        />

        <EstateBalanceSheetAssets
          featureSubscriptionType={featureSubscriptionTypeData.data}
          hiddenAssetColumns={estateBalanceSheet.hiddenAssetColumns}
          estateBalanceSheetId={estateBalanceSheet.id}
          onDataLoaded={handleAssetsDataLoaded}
        />

        <EstateBalanceSheetLiabilities
          featureSubscriptionType={featureSubscriptionTypeData.data}
          hiddenLiabilityColumns={estateBalanceSheet.hiddenLiabilityColumns}
          estateBalanceSheetId={estateBalanceSheet.id}
          onDataLoaded={handleLiabilitiesDataLoaded}
        />
      </div>
    </Fade>
  );

  return hideNav ? content : <HomeLayout>{content}</HomeLayout>;
};

export default EstateBalanceSheetPage;
